import {externalFetch} from '@js/actions/fetch';
import {pushError} from '@js/actions/errorManagement';

import {mapStore} from '@js/stores/mapStore';

import I18n from '@js/modules/translations';
import {FetchTimeout} from '@js/components/modules/constants';
import StorageService from '@js/modules/storage';
import AnalyticsService from '@js/modules/analyticsService';

import Notification from '@js/components/layouts/notification';
import MapInterface from '@js/modules/maps/mapInterface';


const IsochroneMapModule = {
    _isochroneModel: {
        mapPlannerIsochroneUrl: 'https://routing.map-planner.com/isochrone',

        isochroneType: null, // duration or distance
        isochroneValue: 0,

        minIsochronePoints: 8,

        isochroneSourceName: '',

        hasIsochrone: false,
        isochroneCenter: null,

        // Isochrone usage limitations
        mapMaxIsochroneStorage: 'map_max_isochrone_count'
    },

    setIsochrone(type, value) {
        this._isochroneModel.isochroneType = type;
        this._isochroneModel.isochroneValue = value;
    },

    // location: [lat, lng] or {lat:, lng:}
    computeIsochrone(location) {
        if (!this._isochroneModel.isochroneType || !this._isochroneModel.isochroneValue) {
            return;
        }

        if (this._model.mapMaxIsochrone) {
            const mapMaxIsochroneCount = StorageService.getItem(this._isochroneModel.mapMaxIsochroneStorage) || 0;

            if (mapMaxIsochroneCount >= this._model.mapMaxIsochrone) {
                if (this._model.showProductModal) {
                    this._model.showProductModal('map_max_isochrone');
                }

                return;
            }

            StorageService.setItem(this._isochroneModel.mapMaxIsochroneStorage, mapMaxIsochroneCount + 1);
        }

        this._isochroneModel.isochroneCenter = location;

        const isochroneOptions = {};
        if (this._isochroneModel.isochroneType === 'duration') {
            isochroneOptions.timeLimit = this._isochroneModel.isochroneValue;
            isochroneOptions.distanceLimit = -1;

            if (this._isochroneModel.isochroneValue >= 1_800) {
                Notification.info(I18n.t('js.maps.isochrone.duration'));
            }
        } else {
            isochroneOptions.distanceLimit = this._isochroneModel.isochroneValue;
            isochroneOptions.timeLimit = -1;

            if (this._isochroneModel.isochroneValue >= 10_000) {
                Notification.info(I18n.t('js.maps.isochrone.distance'));
            }
        }

        if (mapStore.currentRideType === 'running') {
            isochroneOptions.routeType = 'running';
        } else if (mapStore.currentRideType === 'bike') {
            isochroneOptions.routeType = 'bike';
        } else if (mapStore.currentRideType === 'walk') {
            isochroneOptions.routeType = 'foot_basic';
        } else {
            isochroneOptions.routeType = 'hike_fastest';
        }


        this._getIsochrone(location, isochroneOptions)
            .then((data) => {
                if (!data.polygons?.length || data.polygons[0].geometry.coordinates[0].length < this._isochroneModel.minIsochronePoints) {
                    this._isochroneModel.hasIsochrone = false;

                    if (data.abort) {
                        Notification.error(I18n.t('js.maps.errors.isochrone.timeout'));

                        AnalyticsService.trackIsochroneError('Isochrone computation timeout');
                    } else {
                        Notification.error(I18n.t('js.maps.errors.isochrone.too_few_points'));

                        AnalyticsService.trackIsochroneError(data.message || 'Isochrone too few points');
                    }

                    return;
                }

                this._drawIsochrone(data.polygons);

                this._isochroneModel.hasIsochrone = true;

                this.fitBounds(data.polygons[0].geometry.coordinates[0], {padding: 30});

                setTimeout(() => Notification.hide(), 300);
            })
            .catch((error) => {
                this._isochroneModel.hasIsochrone = false;

                pushError(error);

                Notification.error(I18n.t('js.maps.errors.isochrone.too_few_points'));
            });
    },

    redrawIsochrone() {
        if (!this._isochroneModel.hasIsochrone || !this._isochroneModel.isochroneCenter) {
            return;
        }

        this.computeIsochrone(this._isochroneModel.isochroneCenter);
    },

    clearIsochrone() {
        if (!this._isochroneModel.hasIsochrone || !this._isochroneModel.isochroneCenter) {
            return;
        }

        this._isochroneModel.hasIsochrone = false;
        this._isochroneModel.isochroneCenter = null;

        this.resetMapSource(`${this._isochroneModel.isochroneSourceName}-maine`, true);
        this.resetMapSource(`${this._isochroneModel.isochroneSourceName}-outline`, true);
        this.resetMapSource('isochrone-0');
    },

    /* Private Methods
    ******************** */
    // Parameter 	                Default 	Description
    // profile 		                            The profile to be used for the isochrone calculation.
    // buckets 	                    1 	        Number by which to divide the given time_limit to create buckets nested isochrones of time intervals time_limit-n*time_limit/buckets for n=[0,buckets]. Applies analogously to distance_limit.
    // reverse_flow 	            false 	    If false the flow goes from point to the polygon, if true the flow goes from the polygon inside to the point. Example usage for false: How many potential customer can be reached within 30min travel time from your store vs. true: How many customers can reach your store within 30min travel time. (optional, default to false)
    // point 		                            Specify the start coordinate (required). A string organized as latitude,longitude.
    // time_limit 	                600 	    Specify which time the vehicle should travel. In seconds. (optional, default to 600)
    // distance_limit 	            -1 	        Specify which distance the vehicle should travel. In meter. (optional, default to -1)
    // pt.earliest_departure_time 		        Specify the earliest departure time of the trip. Only applicable and required when profile pt is used. See the public transit section above for more details and other parameters.
    _getIsochrone(location, {
                      timeLimit = 600,
                      distanceLimit = -1,
                      // routeType = 'car',
                      routeType = 'hike_fastest',
                      // routeType = 'foot',
                      buckets = 1
                  } = {},
                  requestOptions = {
                      timeout: FetchTimeout * 8
                  }) {
        let encodedLocation = '';
        if (Array.isArray(location)) {
            encodedLocation += encodeURIComponent(location[0].toFixed(MapInterface.COORDINATES_PRECISION)) + ',' + encodeURIComponent(location[1].toFixed(MapInterface.COORDINATES_PRECISION));
        } else {
            encodedLocation += encodeURIComponent(location.lat.toFixed(MapInterface.COORDINATES_PRECISION)) + ',' + encodeURIComponent(location.lng.toFixed(MapInterface.COORDINATES_PRECISION));
        }

        // Mapbox API for isochrone: https://docs.mapbox.com/api/navigation/isochrone/

        const url = this._isochroneModel.mapPlannerIsochroneUrl + '?point=' + encodedLocation + '&profile=' + routeType + '&time_limit=' + timeLimit + '&distance_limit=' + distanceLimit + '&buckets=' + buckets + '&type=json';

        return externalFetch(url, {}, requestOptions);
    },

    _drawIsochrone(polygons) {
        if (!this._model.map) {
            return;
        }

        polygons.forEach((polygon, index) => {
            if (index !== 0) {
                return;
            }

            const currentIsochrone = this._model.map.getSource(`isochrone-${index}`);
            if (currentIsochrone) {
                currentIsochrone.setData(polygon);
            } else {
                this._model.map.addSource(`isochrone-${index}`, {
                    'type': 'geojson',
                    'data': polygon
                });
            }

            if (!this._model.map.getLayer(`${this._isochroneModel.isochroneSourceName}-maine`)) {
                // Add a new layer to visualize the polygon.
                this._model.map.addLayer({
                    'id': `${this._isochroneModel.isochroneSourceName}-maine`,
                    'type': 'fill',
                    'source': `isochrone-${index}`,
                    'layout': {},
                    'paint': {
                        'fill-color': 'rgba(64, 168, 26)',
                        'fill-opacity': {
                            stops:
                                window.innerWidth > window.settings.medium_screen
                                    ?
                                    [
                                        [8, 0.4],
                                        [13, 0.2],
                                        [15, 0.05]
                                    ]
                                    :
                                    [
                                        [8, 0.4],
                                        [11, 0.2],
                                        [13, 0.1],
                                        [15, 0.05]
                                    ]
                        }
                    }
                });
            }

            if (!this._model.map.getLayer(`${this._isochroneModel.isochroneSourceName}-outline`)) {
                // Add a black outline around the polygon.
                this._model.map.addLayer({
                    'id': `${this._isochroneModel.isochroneSourceName}-outline`,
                    'type': 'line',
                    'source': `isochrone-${index}`,
                    'layout': {},
                    'paint': {
                        'line-color': 'rgba(0, 0, 0, 0.66)',
                        'line-width': 1
                    }
                });
            }
        });
    }
};

export default IsochroneMapModule;
